/**
 * Noticing that a newer desktop build exists, without being asked.
 *
 * The contract on `UpdateControl` used to promise there was no timer anywhere
 * behind the updater, and for *installing* that is still true: nothing here
 * downloads or restarts anything. What changed (at Amit's request, 2026-08-21)
 * is the noticing. This client is left running for days at a time, so a check
 * that only happens when somebody opens the Software card is a check that
 * never happens — and a mail client quietly running a week-old build against
 * a daemon that has moved on is the drift `docs/api-compatibility.md` worries
 * about, wearing a friendly face.
 *
 * So: a check shortly after startup, another every six hours, and one on
 * window focus when the last is stale — focus being the moment somebody came
 * back to the machine, which is the moment a "restart to update" offer costs
 * them the least. What a check finds is only ever *shown* (the banner in the
 * message list); installing stays behind a control someone clicks.
 *
 * Failures are swallowed silently, which is the opposite of this project's
 * usual rule and correct here for the same reason `lib/release.ts` gives:
 * nobody asked. A daemon that is briefly unreachable will be asked again in
 * six hours; the reader has bigger news than a failed background check.
 */

import { signal } from "@preact/signals";

import type { AvailableUpdate, UpdateControl, UpdateProgress } from "./net.types";

/** How often a long-running client looks, unprompted. */
export const CHECK_EVERY_MS = 6 * 60 * 60 * 1000;

/** How long "Later" holds the banner down before it may return. */
export const SNOOZE_MS = 24 * 60 * 60 * 1000;

/** The newest build a background check has found, or null. */
export const found = signal<AvailableUpdate | null>(null);

/** While "Later" is in force: the wall-clock time the snooze ends. */
export const snoozedUntil = signal(0);

/**
 * How far along an install started from anywhere in the app is, or null when
 * none is running.
 *
 * Here rather than in the component that owns the button, because two surfaces
 * offer the same install — the banner above the message list and the Version
 * card in Settings — and a person who starts one from the banner and then opens
 * Settings to watch it must not find a screen that says nothing is happening.
 * One install, one piece of state.
 */
export const installing = signal<UpdateProgress | null>(null);

/**
 * Start the install and keep {@link installing} current for the length of it.
 *
 * Wrapped here rather than called directly so that both surfaces get the state
 * by construction rather than by remembering to set it.
 *
 * On success this does not clear the state: the app is restarting, and a screen
 * that flipped back to "ready to install" in the last moment before the window
 * goes would be reporting the opposite of what just happened. A failure clears
 * it, because then there really is nothing in flight and the buttons have to
 * come back.
 */
export async function install(control: UpdateControl): Promise<void> {
  if (installing.value !== null) return;
  installing.value = { phase: "downloading", downloaded: 0 };
  try {
    await control.install((progress) => {
      installing.value = progress;
    });
  } catch (e) {
    installing.value = null;
    throw e;
  }
}

/**
 * One line of English for a progress report.
 *
 * Here rather than in either component so the banner and the Version card
 * cannot describe the same install differently, and pure so the awkward cases
 * are testable without a download.
 *
 * A percentage needs a total, and the total is a `Content-Length` no server is
 * obliged to send. Without one this says the download is running and declines
 * to invent a fraction — "Downloading…" is honest and a bar stuck at an
 * imaginary number is not. The finished-downloading phase gets its own words
 * because verifying the signature and swapping the binary take a visible moment
 * after the last byte, which is exactly when a bar pinned at 100% reads as a
 * hang.
 */
export function describe(progress: UpdateProgress): string {
  switch (progress.phase) {
    case "downloading": {
      if (progress.total === undefined || progress.total <= 0) return "Downloading the update…";
      const pct = Math.min(100, Math.floor((progress.downloaded / progress.total) * 100));
      return `Downloading the update… ${pct}%`;
    }
    case "installing":
      return "Installing. HydraMail will restart itself.";
    case "failed":
      return "The update could not be installed.";
  }
}

/**
 * Whether a check is owed, as a pure question so it can be tested.
 *
 * `lastCheck === 0` — never checked — is owed immediately; otherwise a check
 * is owed once {@link CHECK_EVERY_MS} has passed. Callers pass `Date.now()`;
 * the parameter exists so the tests do not have to.
 */
export function due(lastCheck: number, now: number): boolean {
  return lastCheck === 0 || now - lastCheck >= CHECK_EVERY_MS;
}

/**
 * Whether the banner should be on screen.
 *
 * Pure for the same reason as {@link due}. A snooze outlasts nothing: a
 * *different* version found while snoozed shows immediately, because the
 * snooze was an answer about the build it was shown for.
 */
export function visible(
  update: AvailableUpdate | null,
  snoozedVersion: string | null,
  until: number,
  now: number,
): boolean {
  if (update === null) return false;
  if (snoozedVersion === update.version && now < until) return false;
  return true;
}

let lastCheck = 0;
let snoozedVersion: string | null = null;
let started = false;

/** What the banner's "Later" button does: hold this version down for a day. */
export function snooze(): void {
  snoozedVersion = found.value?.version ?? null;
  snoozedUntil.value = Date.now() + SNOOZE_MS;
}

/** {@link visible}, applied to the live state. */
export function showing(now: number = Date.now()): boolean {
  return visible(found.value, snoozedVersion, snoozedUntil.value, now);
}

async function check(control: UpdateControl): Promise<void> {
  lastCheck = Date.now();
  try {
    found.value = await control.check();
  } catch {
    // Nobody asked; see the module note. The stale `found` value is kept:
    // a build that was there an hour ago is still the best answer held.
  }
}

/**
 * Begin watching. Idempotent, and a no-op given no control — which is the
 * browser, where the page itself is always the newest version.
 */
export function start(control: UpdateControl | null): void {
  if (control === null || started) return;
  started = true;

  // Shortly after startup rather than at it: the first minute belongs to the
  // session probe and the first sync, and an update found at second five
  // would only be offering a restart to somebody who just started.
  setTimeout(() => void check(control), 60 * 1000);
  setInterval(() => {
    if (due(lastCheck, Date.now())) void check(control);
  }, CHECK_EVERY_MS);

  // The came-back-to-the-machine moment. Guarded by `due`, so a focus every
  // few minutes does not become a request every few minutes.
  window.addEventListener("focus", () => {
    if (due(lastCheck, Date.now())) void check(control);
  });
}
